class CustomSelect extends HTMLElement {
  constructor() {
    super();
    this.ELM_select = this.querySelector('select');
    this.ELM_customOptionsList = this.querySelectorAll('li');
    this.ELM_customSelectSelected = this.querySelector('[data-custom-select-selected]');
    this.ELM_list = this.querySelector('[data-custom-select-list]');
    this.ELM_cardWrapper = this.closest('.card--show-modal-bis');
    this.modalBisId = this.dataset.modalBis;
    this.ELM_modalBis = document.querySelector(`#${this.modalBisId}`);
    this.ELM_modalSelect = this.ELM_modalBis?.querySelector('select');

    // Toggle the dropdown from a click ANYWHERE on the control's box, not just the
    // inner label span. The visible field is the whole <custom-select> host; the
    // `[data-custom-select-selected]` span doesn't fill it (border/padding, and the
    // chevron sit on the host), so binding the click only to the span meant clicks
    // on the surrounding area landed on the host and the dropdown never opened by
    // mouse — keyboard was unaffected. Ignore clicks inside the options list, which
    // select an option (and close) on their own. `.active` on the host is what the
    // CSS uses to reveal `.custom-select__list`, so toggling on the host is correct.
    this.addEventListener('click', (e) => {
      if (e.target.closest('[data-custom-select-list]')) return;
      this.toggle();
    });

    this.ELM_customOptionsList.forEach((elm) => {
      elm.addEventListener('click', (e) => {
        const opt = e.currentTarget;
        const text = opt.innerHTML;

        this.ELM_select.value = opt.dataset.value;
        this.ELM_select.dispatchEvent(new Event('change'));
        if (text) {
          this.ELM_customSelectSelected.querySelector('span').innerHTML = text;
        }

        if (opt.hasAttribute('data-disabled')) {
          this.ELM_cardWrapper?.classList.add('card--oos');
        } else {
          this.ELM_cardWrapper?.classList.remove('card--oos');
        }

        if (this.ELM_modalSelect) {
          this.ELM_modalSelect.value = this.ELM_select.value;
        }

        this.closest('.form-variant-selector')?.querySelector('[name="id"]')?.setAttribute('value', `${this.ELM_select.value}`);

        this.close();
      });
    });

    // a11y (AT1-517 #54): the variant/size dropdown used in quick-add (cart-drawer
    // upsell, PLP cards, cross-sell) was mouse-only — a non-focusable <span> trigger
    // over a display:none native <select>. Expose it as a keyboard listbox so it can
    // be opened, navigated and selected with the keyboard, with a visible focus ring
    // (see frontend/focus-visible.css).
    this.setupKeyboardA11y();
  }

  isOpen() {
    return this.classList.contains('active');
  }

  open() {
    document.querySelectorAll('custom-select.active').forEach((el) => {
      if (el !== this) el.classList.remove('active');
    });
    this.classList.add('active');
    this.ELM_customSelectSelected?.setAttribute('aria-expanded', 'true');
  }

  close() {
    this.classList.remove('active');
    this.ELM_customSelectSelected?.setAttribute('aria-expanded', 'false');
  }

  toggle() {
    if (this.isOpen()) this.close();
    else this.open();
  }

  setupKeyboardA11y() {
    const trigger = this.ELM_customSelectSelected;
    if (!trigger) return;
    const allOptions = [...this.ELM_customOptionsList];
    // options a keyboard user can land on (skip disabled/out-of-stock)
    const navOptions = allOptions.filter((o) => !o.hasAttribute('data-disabled'));

    trigger.setAttribute('tabindex', '0');
    trigger.setAttribute('role', 'button');
    trigger.setAttribute('aria-haspopup', 'listbox');
    trigger.setAttribute('aria-expanded', 'false');
    if (!trigger.hasAttribute('aria-label')) {
      trigger.setAttribute('aria-label', 'Select size');
    }
    this.ELM_list?.setAttribute('role', 'listbox');
    allOptions.forEach((o) => {
      o.setAttribute('role', 'option');
      o.setAttribute('tabindex', '-1');
    });

    const focusOption = (i) => {
      const n = navOptions.length;
      if (!n) return;
      navOptions[((i % n) + n) % n].focus();
    };

    trigger.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar' || e.key === 'ArrowDown') {
        e.preventDefault();
        this.open();
        focusOption(0);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        this.open();
        focusOption(navOptions.length - 1);
      } else if (e.key === 'Escape') {
        this.close();
      }
    });

    allOptions.forEach((opt) => {
      opt.addEventListener('keydown', (e) => {
        const idx = navOptions.indexOf(opt);
        if (e.key === 'ArrowDown') {
          e.preventDefault();
          focusOption(idx + 1);
        } else if (e.key === 'ArrowUp') {
          e.preventDefault();
          focusOption(idx - 1);
        } else if (e.key === 'Home') {
          e.preventDefault();
          focusOption(0);
        } else if (e.key === 'End') {
          e.preventDefault();
          focusOption(navOptions.length - 1);
        } else if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') {
          e.preventDefault();
          opt.click(); // reuse selection logic (also closes)
          trigger.focus();
        } else if (e.key === 'Escape') {
          e.preventDefault();
          this.close();
          trigger.focus();
        } else if (e.key === 'Tab') {
          this.close();
        }
      });
    });

    // Close when focus leaves the whole control (e.g. Tab out).
    this.addEventListener('focusout', (e) => {
      if (!this.contains(e.relatedTarget)) this.close();
    });
  }
}

customElements.define('custom-select', CustomSelect);
